import { useState, useCallback } from 'react';
import { DatagridConfigurable, List, usePermissions, useRefresh, useDataProvider, useGetIdentity } from 'react-admin';
import { toast } from 'react-toastify';

import { feesFieldConfigurations, feesOmittedFields, feeStrings } from '../constants';
import { FeeDataType } from '../utils/DataTypes';
import { exportFeesData } from '../utils/CustomFeesExporter';

import { CustomEditableField, ListActions, ActionsColumn, ConfirmationDialog } from './components';

const Fees = () => {
  const { permissions } = usePermissions();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [savePayload, setSavePayload] = useState<FeeDataType[] | null>(null);
  const [activeEditRowIds, setActiveEditRowIds] = useState<(string | number)[]>([]);
  const [allEditedRows, setAllEditedRows] = useState<Record<string | number, Record<string, any>>>({});

  const handleEdit = (rowId: string | number) => {
    setActiveEditRowIds((prev) => (prev.includes(rowId) ? prev : [...prev, rowId]));
  };

  const handleFieldChange = useCallback((field: string, value: string, rowId: string | number) => {
    setAllEditedRows((prev) => ({
      ...prev,
      [rowId]: { ...(prev[rowId] || {}), [field]: value },
    }));
  }, []);

  const handleSave = async (rowId: string | number) => {
    const changes = allEditedRows[rowId];
    if (!changes || Object.keys(changes).length === 0) {
      toast.info('No edits have been made. Nothing to save.');
      return;
    }
    setSavePayload([{ id: rowId, changes }]);
    setIsModalOpen(true);
  };

  const handleSaveAll = async () => {
    const payload = activeEditRowIds.map((rowId) => ({
      id: rowId,
      changes: allEditedRows[rowId] || {},
    }));
    setSavePayload(payload);
    setIsModalOpen(true);
  };

  const handleCancel = (rowId: string | number) => {
    setActiveEditRowIds((prev) => prev.filter((id) => id !== rowId));
    setAllEditedRows((prev) => {
      const { [rowId]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleCancelAll = () => {
    setActiveEditRowIds([]);
    setAllEditedRows({});
  };

  const handleConfirmSave = async (payload: FeeDataType[]) => {
    try {
      await handleSaveData(payload);
      const idsToRemove = payload.map((item) => item.id);
      setActiveEditRowIds((prev) => prev.filter((id) => !idsToRemove.includes(id)));
      setAllEditedRows((prev) => {
        const updatedRows = { ...prev };
        idsToRemove.forEach((id) => delete updatedRows[id]);
        return updatedRows;
      });
      setIsModalOpen(false);
    } catch (error) {
      toast.error('Error saving changes. Please try again.');
    }
  };

  const handleSaveData = async (feeItems: FeeDataType[]) => {
    const filteredFeeItems = feeItems.filter((fee) => Object.keys(fee.changes).length > 0);

    if (filteredFeeItems.length === 0) {
      toast.info('No actual changes detected. Skipping save.');
      return;
    }
    try {
      const response = await dataProvider.update('fees', {
        data: filteredFeeItems.map((fee) =>
          JSON.stringify({
            id: fee.id,
            ...fee.changes,
            user: identity?.username,
          })
        ),
      } as any);
      refresh();
      const totalUpdated = response?.data?.totalUpdated || 0;
      toast.success(feeStrings.successUpdatingFee(totalUpdated));
    } catch (error) {
      toast.error(feeStrings.errorUpdatingFee);
    }
  };

  return (
    <>
      <List
        perPage={10}
        actions={
          <ListActions
            handleSaveAll={handleSaveAll}
            handleCancelAll={handleCancelAll}
            activeEditRowIds={activeEditRowIds}
          />
        }
        exporter={exportFeesData}
      >
        <DatagridConfigurable bulkActionButtons={false} omit={feesOmittedFields}>
          {feesFieldConfigurations.map((field, index) => (
            <CustomEditableField
              key={`${field.source}-${index}`}
              source={field.source}
              label={field.label}
              editRowIds={activeEditRowIds}
              allEditedRows={allEditedRows}
              handleFieldChange={handleFieldChange}
            />
          ))}
          {permissions.adminPermission && (
            <ActionsColumn
              editRowIds={activeEditRowIds}
              handleEdit={handleEdit}
              handleSave={handleSave}
              handleCancel={handleCancel}
            />
          )}
        </DatagridConfigurable>
      </List>
      <ConfirmationDialog
        isOpen={isModalOpen}
        onConfirm={() => savePayload && handleConfirmSave(savePayload)}
        onCancel={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default Fees;
